import React, { useState } from "react";
import EditIcon from "../../../ui/EditIcon";

import IoModalForm from "./IoModalForm";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import { Button, Col, Row, Space, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { InfoCircleOutlined } from "@ant-design/icons";
import ioIcon from "../../../../assets/images/nurseIcons/io monitoring.svg";
import { convertUtcToIst } from "../../../../helpers/utility";
import {
  TIME_SORT_DIRECTIONS,
  makeTimeSorter,
} from "../../../../helpers/timeSorter";
import useQuery from "../../../../hooks/useQuery";
import useIsMobile from "../../../../hooks/useIsMobile";
import useTabletMonitoringLayout from "../../../../hooks/useTabletMonitoringLayout";

dayjs.extend(utc);
dayjs.extend(timezone);

const getMobileColWidth = (title) => {
  const label = typeof title === "string" ? title : "";
  return Math.max(88, label.length * 7 + 28);
};

const IoBalance = ({ data, refresh, loading, date }) => {
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const isActive = useQuery()?.status === "current";
  const isMobile = useIsMobile();
  const isTablet = useTabletMonitoringLayout();
  const isNarrowTable = isMobile || isTablet;

  const baseColumns = [
    {
      title: "Time",
      dataIndex: "process_time",
      width: isNarrowTable ? 72 : "10%",
      fixed: isNarrowTable ? "left" : undefined,
      sorter: makeTimeSorter("process_time"),
      sortDirections: TIME_SORT_DIRECTIONS,
      render: (value) => (value ? convertUtcToIst(value, false, true) : "-"),
    },
    {
      title: "Oral/Tube",
      dataIndex: "oral_tube_intake",
      width: isNarrowTable ? getMobileColWidth("Oral/Tube") : "10%",
    },
    {
      title: "Flush",
      dataIndex: "flush",
      width: isNarrowTable ? 72 : "10%",
    },
    {
      title: "IV Fluids",
      dataIndex: "iv_fluids",
      width: isNarrowTable ? getMobileColWidth("IV Fluids") : "10%",
    },
    {
      title: "Hourly Intake",
      dataIndex: "hourly_intake",
      width: isNarrowTable ? getMobileColWidth("Hourly Intake") : "10%",
    },
    {
      title: "UO",
      dataIndex: "uo",
      width: isNarrowTable ? 72 : "10%",
    },
    {
      title: "GI Losses",
      dataIndex: "gi_losses",
      width: isNarrowTable ? getMobileColWidth("GI Losses") : "10%",
    },
    {
      title: "Drain",
      dataIndex: "drain",
      width: isNarrowTable ? 72 : "10%",
    },
    {
      title: "Hourly Output",
      dataIndex: "hourly_output",
      width: isNarrowTable ? getMobileColWidth("Hourly Output") : "10%",
    },
    {
      title: "Remarks",
      dataIndex: "additional_info",
      width: isNarrowTable ? 120 : "10%",
    },
  ];

  const columns = [
    ...baseColumns,
    ...(isActive
      ? [
          {
            title: "Action",
            width: isMobile ? 88 : isTablet ? 150 : "12%",
            fixed: isNarrowTable ? "right" : undefined,
            render: (_, record) => {
              return (
                <Space size={8} className="nurse-monitoring-action-cell" wrap={false}>
                  <Button
                    className="nurse-monitoring-edit-btn"
                    onClick={() => {
                      setOpen(true);
                      setEditingKey(record);
                    }}
                  >
                    {isMobile ? (
                      <EditIcon style={{ cursor: "pointer" }} />
                    ) : (
                      <Space>
                        <EditIcon style={{ cursor: "pointer" }} />
                        Edit
                      </Space>
                    )}
                  </Button>
                  <Tooltip
                    placement="topLeft"
                    title={
                      <div>
                        <div>
                          <strong>Updated At:</strong>{" "}
                          {record?.updated_at
                            ? dayjs
                                .utc(record.updated_at)
                                .tz("Asia/Kolkata")
                                .format("DD-MM-YYYY hh:mm A")
                            : "-"}
                        </div>
                        <div style={{ marginTop: "4px" }}>
                          <strong>Processed By:</strong>{" "}
                          {record?.submitted_by_name || "-"}
                        </div>
                        {record?.additional_info ? (
                          <div style={{ marginTop: "4px" }}>
                            <strong>Remarks:</strong> {record.additional_info}
                          </div>
                        ) : null}
                      </div>
                    }
                  >
                    <InfoCircleOutlined
                      className="nurse-monitoring-info-icon"
                      style={{
                        fontSize: "16px",
                        color: "#1677ff",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  const narrowScrollX = isNarrowTable
    ? Math.max(
        columns.reduce(
          (sum, col) =>
            sum + (typeof col.width === "number" ? col.width : 88),
          0
        ),
        1100
      )
    : undefined;

  const tableScroll = isNarrowTable
    ? { x: narrowScrollX }
    : { y: "calc(100vh - 240px)", x: "400px" };

  const totals = (data || []).reduce(
    (acc, row) => {
      const toNumber = (v) => (v ? Number(v) : 0);

      const intake =
        toNumber(row.oral_tube_intake) +
        toNumber(row.flush) +
        toNumber(row.iv_fluids);

      const output =
        toNumber(row.uo) + toNumber(row.gi_losses) + toNumber(row.drain);

      acc.intake += intake;
      acc.output += output;
      return acc;
    },
    { intake: 0, output: 0 }
  );

  const net = totals.intake - totals.output;
  return (
    <>
      {data?.length > 0 ? (
        <>
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              gap: 16,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: 600 }}>
              Total Intake: {totals.intake.toFixed(1)}
            </span>
            <span style={{ fontWeight: 600 }}>
              Total Output: {totals.output.toFixed(1)}
            </span>
          </div>
          <div className="nurse-monitoring-table-scroll">
            <Table
              className="nurse-monitoring-data-table ant-table-container ant-table-header"
              tableLayout={isNarrowTable ? "fixed" : undefined}
              scroll={tableScroll}
              bordered
              dataSource={data}
              columns={columns}
              rowClassName="editable-row"
              pagination={false}
              loading={loading}
            />
          </div>
          {open && (
            <IoModalForm
              open={open}
              setOpen={setOpen}
              refresh={refresh}
              editData={editingKey}
              date={date}
            />
          )}
        </>
      ) : (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"No Monitoring Data Available"}
              subtitle={
                "Please check back later or ensure all relevant data has been recorded."
              }
              image={ioIcon}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default IoBalance;
